import PushPinIcon from '@mui/icons-material/PushPin';
import { DialogTitle } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import {
  IComposableDashboardCollection,
  IProfileDashboard,
  IComposableDashboardCollectionsContext,
} from 'wisestep-api';
import { WsButton, WsDialog, WsDrawer } from 'wisestep-base';

import { ConfirmDeleteDialog, IconForDialog } from '../../../components';
import { useUserContext } from '../../../context';
import ErrorLogger from '../../../providers/ErrorLogger';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import ConfirmationDialog from '../../clientDetails/components/ConfirmationDialog';
import { StyledGrid } from '../../clientListingPage/components/ClientList';
import AnalyticsContentLayout from '../components/AnalyticsContentLayout';
import ShareDashboardDialog from '../components/dashboard/ShareDashboardDialog';
import TagDashboardDialog from '../components/dashboard/TagDashboardDialog';
import SaveCollectionDialog from '../components/dashboardCollection/SaveCollectionDialog';
import {
  assignUnassignPromptText,
  defaultPinnedDashboardCollection,
  pinUnpinPromptText,
} from '../helper/getDefaultDashboardValues';
import useCollectionsOptions from '../hooks/useCollectionsOptions';
import { useComposableDashboardCollectionsContext } from '../hooks/useComposableDashboardCollectionsContext';
import useDeleteCollection from '../hooks/useDeleteCollection';
import useFetchAllProfileDashboards from '../hooks/useFetchAllProfileDashboards';
import useFetchTeamNames from '../hooks/useFetchTeamNames';
import useHandleAssignFlow, { AssignDialogType } from '../hooks/useHandleAssignFlow';
import usePinnedDashboardCollections from '../hooks/usePinnedDashboardCollections';
import useRenameDashboard from '../hooks/useRenameDashboard';
import useTagDashboardCollection from '../hooks/useTagDashboardCollection';

type actionType =
  | 'pin'
  | 'unpin'
  | 'assign'
  | 'share'
  | 'rename'
  | 'delete'
  | 'tag'
  | 'assignToClients'
  | 'unAssignToClients'
  | 'assignToClientSpoc'
  | 'unAssignToClientSpoc'
  | 'assignToAccountManagerOU'
  | 'unAssignToAccountManagerOU'
  | 'assignToTeamsOU'
  | 'unAssignToTeamsOU'
  | 'assignToTeams'
  | 'unAssignToTeams'
  | 'assignToAccountManagers'
  | 'unAssignToAccountManagers'
  | 'assignToBusinessUnit'
  | 'unAssignToBusinessUnit'
  | 'assignToClientUnit'
  | 'unAssignToClientUnit'
  | 'assignToClientContactUnit'
  | 'unAssignToClientContactUnit'
  | 'assignToUsers'
  | 'unAssignToUsers'
  | 'assignToJobs'
  | 'unAssignToJobs';

const DashboardCollectionsOptions = () => {
  const navigate = useNavigate();
  const [isAssignFlow, setIsAssignFlow] = useState<boolean>(false);
  const {
    pinnedDashboards,
    selectedPinnedDashboard,
    setSelectedPinnedDashboard,
    updateDashboardStatus,
    openShareDialog,
    setOpenShareDialog,
    handlePinningDashboard,
    openPinDialog,
    setOpenPinDialog,
  } = usePinnedDashboardCollections(isAssignFlow);
  const [selectedProfileDashboard, setSelectedProfileDashboard] = useState<IProfileDashboard>(
    defaultPinnedDashboardCollection
  );
  const {
    dashboardCollections,
    selectedDashboardCollection,
    setSelectedCollectionByIndex,
    refetchDashboardCollections,
  } = useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const { allowedTeams, fetchTeamNames } = useFetchTeamNames();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const loggedInUser = useUserContext().userDTO;
  const { onRenameDashboard, openUpdateDialog, setOpenUpdateDialog } = useRenameDashboard();
  const { onDeleteCollection, setOpenDeleteDialog, openDeleteDialog } = useDeleteCollection();
  const { profileDashboards, setProfileDashboards } = useFetchAllProfileDashboards();
  const { handleTagDashboard, openTagDialog, setOpenTagDialog } = useTagDashboardCollection(
    profileDashboards,
    setProfileDashboards
  );

  const {
    assignUnassignDialog,
    setAssignUnassignDialog,
    handleAssignUnassignOperation,
    assignedTo,
  } = useHandleAssignFlow();

  const handleOnClick = useCallback(
    async (index: number, action: actionType, creator?: boolean) => {
      setSelectedCollectionByIndex(index);
      const dashboard = dashboardCollections?.[index];
      if (!dashboard) {
        setNotification(
          failed('Dashboard collection has not been selected properly. Please try again later!')
        );
        return;
      }
      const pinDashboard = pinnedDashboards?.find(
        (pd) => pd.dashboardCollection.id === dashboard?.id && pd.filters == null
      );
      setSelectedPinnedDashboard(
        pinDashboard?.id ? pinDashboard : defaultPinnedDashboardCollection
      );
      const taggedDashboard = profileDashboards?.find(
        ({ dashboardCollection }) => dashboardCollection.id === dashboard.id
      );
      if (taggedDashboard?.id) setSelectedProfileDashboard(taggedDashboard);

      switch (action) {
        case 'pin':
        case 'unpin':
          creator
            ? setOpenPinDialog(true)
            : setNotification(
                failed('Sorry, You cannot unpin dashboard collection as it is assigned to you !!')
              );
          break;
        case 'share': {
          const isTeamNamesFetched = await fetchTeamNames(action, dashboard.sharedWithTeams);
          if (isTeamNamesFetched) {
            setIsAssignFlow(false);
            setOpenShareDialog(true);
          }
          break;
        }
        case 'tag': {
          const isTeamNamesFetched = await fetchTeamNames(action, pinDashboard?.teams);
          if (isTeamNamesFetched) {
            setOpenTagDialog(true);
          }
          break;
        }
        case 'assign': {
          const isTeamNamesFetched = await fetchTeamNames(action, pinDashboard?.teams);
          if (isTeamNamesFetched) {
            setIsAssignFlow(true);
            setOpenShareDialog(true);
          }
          break;
        }
        case 'rename':
          setOpenUpdateDialog(true);
          break;
        case 'delete':
          setOpenDeleteDialog(true);
          break;
        case 'assignToClients':
        case 'unAssignToClients':
          creator
            ? setAssignUnassignDialog(AssignDialogType.Clients)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToClientSpoc':
        case 'unAssignToClientSpoc':
          creator
            ? setAssignUnassignDialog(AssignDialogType.ClientSpoc)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToAccountManagerOU':
        case 'unAssignToAccountManagerOU':
          creator
            ? setAssignUnassignDialog(AssignDialogType.AccountManagerOU)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToTeamsOU':
        case 'unAssignToTeamsOU':
          creator
            ? setAssignUnassignDialog(AssignDialogType.TeamsOU)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToTeams':
        case 'unAssignToTeams':
          creator
            ? setAssignUnassignDialog(AssignDialogType.Teams)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToAccountManagers':
        case 'unAssignToAccountManagers':
          creator
            ? setAssignUnassignDialog(AssignDialogType.AccountManagers)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToBusinessUnit':
        case 'unAssignToBusinessUnit':
          creator
            ? setAssignUnassignDialog(AssignDialogType.BusinessDefinition)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToClientUnit':
        case 'unAssignToClientUnit':
          creator
            ? setAssignUnassignDialog(AssignDialogType.ClientUnit)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToClientContactUnit':
        case 'unAssignToClientContactUnit':
          creator
            ? setAssignUnassignDialog(AssignDialogType.ClientContactUnit)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToUsers':
        case 'unAssignToUsers':
          creator
            ? setAssignUnassignDialog(AssignDialogType.Users)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        case 'assignToJobs':
        case 'unAssignToJobs':
          creator
            ? setAssignUnassignDialog(AssignDialogType.Jobs)
            : setNotification(failed('Sorry, You cannot assign this dashboard collection!!'));
          break;
        default:
          creator
            ? setAssignUnassignDialog(AssignDialogType.Clients)
            : setNotification(failed('Sorry, You cannot unassign this dashboard collection!!'));
      }
    },
    [
      dashboardCollections,
      fetchTeamNames,
      pinnedDashboards,
      profileDashboards,
      setAssignUnassignDialog,
      setNotification,
      setOpenDeleteDialog,
      setOpenPinDialog,
      setOpenShareDialog,
      setOpenTagDialog,
      setOpenUpdateDialog,
      setSelectedCollectionByIndex,
      setSelectedPinnedDashboard,
    ]
  );

  useEffect(() => {
    refetchDashboardCollections();
  }, [refetchDashboardCollections]);

  const { options, title } = useCollectionsOptions(handleOnClick, pinnedDashboards);

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <StyledGrid item xs={12}>
        <AnalyticsContentLayout
          key="master_dashboard"
          options={options ?? []}
          title={title}
          headerButton={
            <WsButton
              onClick={() => {
                setSelectedCollectionByIndex(-1);
                navigate('/workspace-users-and-teams/master-dashboards/view');
              }}
            >
              Create new Dashboard Collection
            </WsButton>
          }
          selectedDashboardId="master"
        />
        <WsDialog openDialog={openPinDialog}>
          <DialogTitle>
            <IconForDialog icon={<PushPinIcon />} />
          </DialogTitle>
          <ConfirmationDialog
            prompt={pinUnpinPromptText(
              selectedPinnedDashboard.users.includes(loggedInUser?.email ?? ''),
              selectedDashboardCollection.name
            )}
            onConfirm={() => handlePinningDashboard(selectedDashboardCollection?.id ?? '', null)}
            onSkip={() => {
              setSelectedPinnedDashboard(defaultPinnedDashboardCollection);
              setOpenPinDialog(false);
            }}
            confirmText={
              selectedPinnedDashboard.users.includes(loggedInUser?.email ?? '') ? 'Unpin' : 'Pin'
            }
            skipText="Cancel"
          />
        </WsDialog>
        <WsDialog openDialog={Boolean(assignUnassignDialog)}>
          <DialogTitle>
            <IconForDialog icon={<PushPinIcon />} />
          </DialogTitle>
          <ConfirmationDialog
            prompt={assignUnassignPromptText(
              assignedTo.includes(assignUnassignDialog),
              assignUnassignDialog,
              selectedDashboardCollection?.name ?? ''
            )}
            onConfirm={() => handleAssignUnassignOperation(assignUnassignDialog)}
            onSkip={() => setAssignUnassignDialog(AssignDialogType.None)}
            confirmText={assignedTo.includes(assignUnassignDialog) ? 'Unassign' : 'Assign'}
            skipText="Cancel"
          />
        </WsDialog>
        <WsDrawer size="small" open={openShareDialog} onClose={() => setOpenShareDialog(false)}>
          <ShareDashboardDialog
            dashboard={selectedDashboardCollection}
            onCloseDialog={() => setOpenShareDialog(false)}
            onSave={(dashboard) =>
              updateDashboardStatus(dashboard as IComposableDashboardCollection)
            }
            isAssignFlow={isAssignFlow}
            sharedToTeams={allowedTeams}
            selectedPinnedDashboard={selectedPinnedDashboard}
          />
        </WsDrawer>

        {openDeleteDialog && (
          <ConfirmDeleteDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            onConfirm={onDeleteCollection}
            deletingItem={`${selectedDashboardCollection.name}`}
          />
        )}
        {openUpdateDialog && (
          <SaveCollectionDialog
            setSaveModal={setOpenUpdateDialog}
            saveCollection={onRenameDashboard}
            update={true}
            customUpdateText="Update Dashboard Collection Name"
          />
        )}
        {openTagDialog && (
          <TagDashboardDialog
            dashboardCollection={selectedDashboardCollection}
            onCloseDialog={() => setOpenTagDialog(false)}
            onSave={handleTagDashboard}
            sharedToTeams={allowedTeams}
            selectedProfileDashboard={selectedProfileDashboard}
          />
        )}
      </StyledGrid>
    </ErrorBoundary>
  );
};

export default DashboardCollectionsOptions;
